/**
 * Active (accepted/manual) memory entries — on/off toggle, edit text, or delete.
 * Retired entries stay in the list (dimmed) so the toggle can turn them back on.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Loader2, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useDeleteMemoryEntry, useMemoryErrorToast, useUpdateMemoryEntry } from '../useUserMemoryQueries';
import { memoryCategoryChipVariants, memoryStatusAccentVariants } from '../userMemory.styles';
import { MEMORY_CATEGORY_LABELS, MEMORY_INSTRUCTION_MAX_LENGTH, type MemoryEntry } from '../userMemory.types';

interface ActiveMemoryListProps {
  entries: MemoryEntry[];
}

interface EditMemoryDialogProps {
  entry: MemoryEntry;
  onOpenChange: (open: boolean) => void;
}

const EditMemoryDialog = ({ entry, onOpenChange }: EditMemoryDialogProps) => {
  const [text, setText] = useState(entry.instruction_text);
  const updateMutation = useUpdateMemoryEntry();
  const { toast } = useToast();
  const onError = useMemoryErrorToast();

  const handleSave = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    updateMutation.mutate(
      { id: entry.id, payload: { instruction_text: trimmed } },
      {
        onSuccess: () => {
          toast({ title: 'Minni uppfært' });
          onOpenChange(false);
        },
        onError,
      }
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Breyta minni</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MEMORY_INSTRUCTION_MAX_LENGTH))}
            maxLength={MEMORY_INSTRUCTION_MAX_LENGTH}
            rows={4}
            className="resize-none"
            aria-label="Leiðbeiningar"
          />
          <div className="flex justify-end text-muted-foreground text-xs">
            {text.length}/{MEMORY_INSTRUCTION_MAX_LENGTH}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={updateMutation.isPending}>
            Hætta við
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending || !text.trim()}>
            {updateMutation.isPending ? 'Vista...' : 'Vista breytingar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ActiveMemoryCardProps {
  entry: MemoryEntry;
  onEdit: (entry: MemoryEntry) => void;
  onRequestDelete: (entry: MemoryEntry) => void;
}

const ActiveMemoryCard = ({ entry, onEdit, onRequestDelete }: ActiveMemoryCardProps) => {
  const updateMutation = useUpdateMemoryEntry();
  const { toast } = useToast();
  const onError = useMemoryErrorToast();

  const isActive = entry.status === 'active';
  const isToggling = updateMutation.isPending && updateMutation.variables?.id === entry.id;

  const handleToggle = (enabled: boolean) => {
    updateMutation.mutate(
      { id: entry.id, payload: { status: enabled ? 'active' : 'retired' } },
      {
        onSuccess: () => toast({ title: enabled ? 'Minni virkt' : 'Minni slökkt' }),
        onError,
      }
    );
  };

  return (
    <div className={cn(memoryStatusAccentVariants({ status: isActive ? 'active' : 'retired' }))}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <span className={cn(memoryCategoryChipVariants({ category: entry.category }))}>
              {MEMORY_CATEGORY_LABELS[entry.category]}
            </span>
            <span className="text-muted-foreground text-xs">{entry.template_id ? 'Bundið sniðmáti' : 'Almennt'}</span>
            {entry.source === 'manual' && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-medium text-slate-600 text-xs">
                Handvirkt
              </span>
            )}
          </div>
          <p className={cn('text-sm', isActive ? 'text-slate-900' : 'text-muted-foreground')}>
            {entry.instruction_text}
          </p>
          <p className="text-muted-foreground text-xs">
            {entry.scope === 'global' ? 'Sameiginlegt minni · Þitt eintak' : 'Persónulegt minni'}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Switch
            checked={isActive}
            onCheckedChange={handleToggle}
            disabled={isToggling}
            aria-label={isActive ? 'Slökkva á minni' : 'Kveikja á minni'}
          />
          {/* Payload edits are only allowed while the entry is active (BFF gate) */}
          <Button variant="ghost" size="sm" onClick={() => onEdit(entry)} disabled={!isActive} aria-label="Breyta">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRequestDelete(entry)}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            aria-label="Eyða"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const ActiveMemoryList = ({ entries }: ActiveMemoryListProps) => {
  const [editingEntry, setEditingEntry] = useState<MemoryEntry | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<MemoryEntry | null>(null);
  const deleteMutation = useDeleteMemoryEntry();
  const { toast } = useToast();
  const onDeleteError = useMemoryErrorToast();

  const handleConfirmDelete = () => {
    if (!entryToDelete) return;
    deleteMutation.mutate(entryToDelete.id, {
      onSuccess: () => {
        toast({ title: 'Minni fjarlægt' });
        setEntryToDelete(null);
      },
      onError: (error) => {
        onDeleteError(error);
        setEntryToDelete(null);
      },
    });
  };

  return (
    <div className="space-y-3">
      <h3 className="font-medium text-slate-900 text-sm">Virkt minni</h3>
      {entries.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm">
          Engar reglur virkjaðar. Samþykktar tillögur og handvirkt búnar til reglur birtast hér.
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <ActiveMemoryCard
              key={entry.id}
              entry={entry}
              onEdit={setEditingEntry}
              onRequestDelete={setEntryToDelete}
            />
          ))}
        </div>
      )}

      {editingEntry && (
        <EditMemoryDialog
          key={editingEntry.id}
          entry={editingEntry}
          onOpenChange={(open) => !open && setEditingEntry(null)}
        />
      )}

      <AlertDialog open={entryToDelete !== null} onOpenChange={(open) => !open && setEntryToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eyða minni?</AlertDialogTitle>
            <AlertDialogDescription>
              Þessari aðgerð er ekki afturkræf. Reglan verður fjarlægð og ekki lengur notuð í samantektum.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Hætta við</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Eyða
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
